import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Stethoscope, UserCog, Lock, Mail, User, BadgeCheck, Key, Building, Eye, EyeOff } from 'lucide-react';

const LOGO_URL = '/logo.png';

export default function LoginPage() {
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Login form state
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [mfaRequired, setMfaRequired] = useState(false);
    const [mfaCode, setMfaCode] = useState('');

    // Register form state
    const [registerData, setRegisterData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        role: 'doctor',
        gmc_number: '',
        specialty: '',
        usic: '',
        csic: ''
    });

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await login(loginEmail, loginPassword, mfaCode || null);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            const detail = error.response?.data?.detail;
            // Detect MFA-required response: backend returns 401 with an object detail
            if (detail && typeof detail === 'object' && detail.mfa_required) {
                setMfaRequired(true);
                toast.info('Enter the 6-digit code from your authenticator app');
            } else {
                const message = typeof detail === 'string' ? detail : 'Invalid credentials';
                toast.error(message);
                if (mfaRequired && (message.includes('MFA') || message.includes('code'))) {
                    setMfaCode('');
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (registerData.password !== registerData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setIsLoading(true);
        try {
            const { confirmPassword, ...userData } = registerData;
            const result = await register(userData);
            toast.success('Registration successful!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex">
            {/* Left Panel - Branding */}
            <motion.div 
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="hidden lg:flex lg:w-1/2 gradient-primary relative overflow-hidden"
            >
                <div className="absolute inset-0 bg-black/10" />
                <div className="relative z-10 flex flex-col justify-between p-12 text-white">
                    <div>
                        <img src={LOGO_URL} alt="FendanaCor" className="w-24 h-24 rounded-2xl shadow-xl mb-8" />
                        <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            FendanaCor
                        </h1>
                        <p className="text-xl text-teal-100 mb-8">
                            Ortho referrals without pain
                        </p>
                    </div>
                    
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-white/20 rounded-lg">
                                <Stethoscope className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Evidence-Based Screening</h3>
                                <p className="text-teal-100 text-sm">BOA, NICE, NHS guidelines integrated</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-white/20 rounded-lg">
                                <UserCog className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Smart Urgency Scoring</h3>
                                <p className="text-teal-100 text-sm">Automated triage with clinical validation</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="p-3 bg-white/20 rounded-lg">
                                <BadgeCheck className="w-6 h-6" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-lg">Multi-Society Standards</h3>
                                <p className="text-teal-100 text-sm">BOFAS, BESS, BSSH, RCSEng compliant</p>
                            </div>
                        </div>
                    </div>

                    <p className="text-teal-200 text-sm">
                        Supporting NHS clinicians with intelligent referral management
                    </p>
                </div>
            </motion.div>

            {/* Right Panel - Auth Forms */}
            <motion.div 
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="flex-1 flex items-center justify-center p-8"
            >
                <div className="w-full max-w-md">
                    {/* Mobile Logo */}
                    <div className="lg:hidden flex items-center gap-4 mb-8 justify-center">
                        <img src={LOGO_URL} alt="FendanaCor" className="w-16 h-16 rounded-xl shadow-lg" />
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                FendanaCor
                            </h1>
                            <p className="text-sm text-slate-500">Ortho referrals without pain</p>
                        </div>
                    </div>

                    <Card className="border-slate-200 shadow-xl">
                        <CardHeader className="text-center pb-2">
                            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Welcome
                            </CardTitle>
                            <CardDescription>
                                Sign in to access the referral system
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="login" className="w-full">
                                <TabsList className="grid w-full grid-cols-2 mb-6">
                                    <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                                    <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                                </TabsList>

                                <TabsContent value="login">
                                    <form onSubmit={handleLogin} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="login-email" className="label-clinical">Email Address</Label>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center justify-center w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0">
                                                    <Mail className="h-5 w-5 text-teal-600" />
                                                </div>
                                                <Input
                                                    id="login-email"
                                                    type="email"
                                                    placeholder="doctor@nhs.net"
                                                    value={loginEmail}
                                                    onChange={(e) => setLoginEmail(e.target.value)}
                                                    className="input-clinical flex-1"
                                                    data-testid="login-email-input"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="login-password" className="label-clinical">Password</Label>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center justify-center w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0">
                                                    <Lock className="h-5 w-5 text-teal-600" />
                                                </div>
                                                <div className="relative flex-1">
                                                    <Input
                                                        id="login-password"
                                                        type={showLoginPassword ? "text" : "password"}
                                                        placeholder="Enter your password"
                                                        value={loginPassword}
                                                        onChange={(e) => setLoginPassword(e.target.value)}
                                                        className="pr-12 input-clinical w-full"
                                                        data-testid="login-password-input"
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                                        data-testid="login-password-toggle"
                                                        tabIndex={-1}
                                                    >
                                                        {showLoginPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        {mfaRequired && (
                                            <div className="space-y-2" data-testid="mfa-code-section">
                                                <Label htmlFor="mfa-code" className="label-clinical">
                                                    Authenticator code
                                                </Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center justify-center w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0">
                                                        <Key className="h-5 w-5 text-teal-600" />
                                                    </div>
                                                    <Input
                                                        id="mfa-code"
                                                        placeholder="6-digit code"
                                                        value={mfaCode}
                                                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                        className="input-clinical flex-1 tracking-widest text-center"
                                                        data-testid="mfa-code-input"
                                                        autoComplete="one-time-code"
                                                        inputMode="numeric"
                                                        pattern="\d{6}"
                                                        maxLength={6}
                                                        autoFocus
                                                    />
                                                </div>
                                                <p className="text-xs text-slate-500">
                                                    Enter the 6-digit code from Google Authenticator, Authy or 1Password.
                                                </p>
                                            </div>
                                        )}
                                        <Button 
                                            type="submit" 
                                            className="w-full btn-primary"
                                            disabled={isLoading || (mfaRequired && mfaCode.length !== 6)}
                                            data-testid="login-submit-btn"
                                        >
                                            {isLoading ? 'Signing in...' : (mfaRequired ? 'Verify & Sign In' : 'Sign In')}
                                        </Button>
                                    </form>
                                </TabsContent>

                                <TabsContent value="register">
                                    <form onSubmit={handleRegister} className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="register-name" className="label-clinical">Full Name</Label>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center justify-center w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0">
                                                    <User className="h-5 w-5 text-teal-600" />
                                                </div>
                                                <Input
                                                    id="register-name"
                                                    placeholder="Dr. John Smith"
                                                    value={registerData.full_name}
                                                    onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                                                    className="input-clinical flex-1"
                                                    data-testid="register-name-input"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-email" className="label-clinical">Email Address</Label>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center justify-center w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0">
                                                    <Mail className="h-5 w-5 text-teal-600" />
                                                </div>
                                                <Input
                                                    id="register-email"
                                                    type="email"
                                                    placeholder="doctor@nhs.net"
                                                    value={registerData.email}
                                                    onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                                                    className="input-clinical flex-1"
                                                    data-testid="register-email-input"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="register-role" className="label-clinical">Role</Label>
                                            <Select 
                                                value={registerData.role} 
                                                onValueChange={(value) => setRegisterData({...registerData, role: value})}
                                            >
                                                <SelectTrigger data-testid="register-role-select">
                                                    <SelectValue placeholder="Select your role" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="doctor">Referring Doctor (GP)</SelectItem>
                                                    <SelectItem value="consultant">Consultant Surgeon</SelectItem>
                                                    <SelectItem value="institution_admin">Institution Admin</SelectItem>
                                                    <SelectItem value="nhs_manager">NHS Manager</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        
                                        {/* Subscription Codes */}
                                        <div className="pt-4 border-t border-slate-100 space-y-4">
                                            <p className="text-xs text-slate-500">
                                                Enter your subscription codes (provided by your organization)
                                            </p>
                                            <div className="space-y-2">
                                                <Label htmlFor="register-usic" className="label-clinical">USIC (User Code)</Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center justify-center w-10 h-10 bg-slate-50 border border-slate-200 rounded-lg flex-shrink-0">
                                                        <Key className="w-5 h-5 text-slate-500" />
                                                    </div>
                                                    <Input
                                                        id="register-usic"
                                                        placeholder="USIC-XXXXXXXXXXXX"
                                                        value={registerData.usic}
                                                        onChange={(e) => setRegisterData({...registerData, usic: e.target.value})}
                                                        className="input-clinical font-mono flex-1"
                                                        data-testid="register-usic-input"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="register-csic" className="label-clinical">CSIC (Company Code)</Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center justify-center w-10 h-10 bg-slate-50 border border-slate-200 rounded-lg flex-shrink-0">
                                                        <Building className="w-5 h-5 text-slate-500" />
                                                    </div>
                                                    <Input
                                                        id="register-csic"
                                                        placeholder="CSIC-XXXXXXXX"
                                                        value={registerData.csic}
                                                        onChange={(e) => setRegisterData({...registerData, csic: e.target.value})}
                                                        className="input-clinical font-mono flex-1"
                                                        data-testid="register-csic-input"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="register-password" className="label-clinical">Password</Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center justify-center w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0">
                                                        <Lock className="h-5 w-5 text-teal-600" />
                                                    </div>
                                                    <div className="relative flex-1">
                                                        <Input
                                                            id="register-password"
                                                            type={showRegisterPassword ? "text" : "password"}
                                                            placeholder="••••••••"
                                                            value={registerData.password}
                                                            onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                                                            className="input-clinical pr-12 w-full"
                                                            data-testid="register-password-input"
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                                            data-testid="register-password-toggle"
                                                            tabIndex={-1}
                                                        >
                                                            {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                        </button>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-slate-500 pl-12" data-testid="password-policy-hint">
                                                    Minimum 12 characters with upper, lower, digit and symbol.
                                                </p>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="register-confirm" className="label-clinical">Confirm Password</Label>
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center justify-center w-10 h-10 bg-teal-50 border border-teal-200 rounded-lg flex-shrink-0">
                                                        <Lock className="h-5 w-5 text-teal-600" />
                                                    </div>
                                                    <div className="relative flex-1">
                                                        <Input
                                                            id="register-confirm"
                                                            type={showConfirmPassword ? "text" : "password"}
                                                            placeholder="••••••••"
                                                            value={registerData.confirmPassword}
                                                            onChange={(e) => setRegisterData({...registerData, confirmPassword: e.target.value})}
                                                            className="input-clinical pr-12 w-full"
                                                            data-testid="register-confirm-input"
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                                            data-testid="register-confirm-toggle"
                                                            tabIndex={-1}
                                                        >
                                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <Button 
                                            type="submit" 
                                            className="w-full btn-primary"
                                            disabled={isLoading}
                                            data-testid="register-submit-btn"
                                        >
                                            {isLoading ? 'Creating account...' : 'Create Account'}
                                        </Button>
                                    </form>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>

                    <p className="text-center text-sm text-slate-500 mt-6">
                        By signing in, you agree to comply with NHS data protection policies
                    </p>
                </div>
            </motion.div>
        </div>
    );
}
